import React, { useMemo } from 'react';
import { Grid, Card, CardContent, Typography, Box } from '@mui/material';
import { Bar, Doughnut } from 'react-chartjs-2';
import { formatNum } from './dashboardUtils';
import { isLeadStatusItem, orderPaymentMetric } from './dashboardConfig';

const PALETTE = ['#1976d2', '#2e7d32', '#ed6c02', '#d32f2f', '#0288d1', '#9c27b0', '#ff9800', '#03a9f4'];
const ORDER_COLORS = { paid: '#2e7d32', partial: '#0288d1', unpaid: '#ed6c02' };

const doughnutOptions = { responsive: true, maintainAspectRatio: false, plugins: { legend: { position: 'bottom' } }, cutout: '55%' };
const barOptions = { responsive: true, maintainAspectRatio: false, plugins: { legend: { position: 'top' } }, scales: { y: { beginAtZero: true } } };

const hasValues = (values) => values.some((v) => v > 0);

function ChartCard({ title, emptyText, children, lg }) {
  return (
    <Grid item xs={12} lg={lg} sx={{ minWidth: 0 }}>
      <Card sx={{ overflow: 'hidden', height: '100%' }}>
        <CardContent>
          <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>{title}</Typography>
          <Box sx={{ height: { xs: 240, sm: 280 }, minWidth: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {children ?? (
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', px: 2 }}>{emptyText}</Typography>
            )}
          </Box>
        </CardContent>
      </Card>
    </Grid>
  );
}

const doughnutData = (labels, data, backgroundColor) => ({
  labels,
  datasets: [{ data, backgroundColor, borderWidth: 0 }],
});

/**
 * Charts derived from whatever the API sent: each one is shown when its source block is in
 * the payload (`teams`, lead summary, order summary), with an empty state while it has no data.
 */
export default function DashboardCharts({ teams, leadsSummary, sales }) {
  const leadStatus = useMemo(() => {
    const rows = (leadsSummary ?? []).filter(isLeadStatusItem);
    const values = rows.map((i) => formatNum(i.value));
    return hasValues(values) ? doughnutData(rows.map((i) => i.title), values, rows.map((_, i) => PALETTE[i % PALETTE.length])) : null;
  }, [leadsSummary]);

  const orderStatus = useMemo(() => {
    const rows = (sales ?? [])
      .map((i) => ({ title: i.title, metric: orderPaymentMetric(i), value: formatNum(i.value) }))
      .filter((r) => r.metric);
    const values = rows.map((r) => r.value);
    return hasValues(values) ? doughnutData(rows.map((r) => r.title), values, rows.map((r) => ORDER_COLORS[r.metric])) : null;
  }, [sales]);

  const teamData = useMemo(() => {
    if (!teams?.length) return null;
    return {
      labels: teams.map((t) => t.name ?? '—'),
      datasets: [
        { label: 'Total Leads', data: teams.map((t) => formatNum(t.total_leads)), backgroundColor: 'rgba(25, 118, 210, 0.75)', borderRadius: 6 },
        { label: 'Leads In Process', data: teams.map((t) => formatNum(t.in_process)), backgroundColor: 'rgba(2, 136, 209, 0.6)', borderRadius: 6 },
        { label: 'Sales (Paid Orders)', data: teams.map((t) => formatNum(t.sales_generated)), backgroundColor: 'rgba(46, 125, 50, 0.75)', borderRadius: 6 },
      ],
    };
  }, [teams]);

  const showTeams = Array.isArray(teams);
  const showLeads = Array.isArray(leadsSummary);
  const showOrders = Array.isArray(sales);
  if (!showTeams && !showLeads && !showOrders) return null;

  const rightCharts = Number(showLeads) + Number(showOrders);

  return (
    <Grid container spacing={3} sx={{ mb: 3 }}>
      {showTeams && (
        <ChartCard title="Team performance" lg={rightCharts ? 7 : 12} emptyText="No data found for the selected period.">
          {teamData && <Bar data={teamData} options={barOptions} />}
        </ChartCard>
      )}
      {showLeads && (
        <ChartCard title="Leads status (selected period)" lg={showTeams ? 5 : 6} emptyText="No data found for the selected period.">
          {leadStatus && <Doughnut data={leadStatus} options={doughnutOptions} />}
        </ChartCard>
      )}
      {showOrders && (
        <ChartCard title="Order payment status" lg={showTeams ? 12 : 6} emptyText="No data found for the selected period.">
          {orderStatus && <Doughnut data={orderStatus} options={doughnutOptions} />}
        </ChartCard>
      )}
    </Grid>
  );
}
